/**
 * Which permission a page group requires.
 *
 * One definition, read by the navigation, the route guard and the API, because
 * three copies of an access rule become three different access rules. The
 * database is still the authority on what a person *holds* — this only says
 * what each part of the product asks for.
 *
 * ## Records versus pages
 *
 * These are the *pages*. Which **records** a person sees is a separate
 * mechanism and a better one: `iam.access_scope` narrows every query to their
 * span of control and `iam.field_policy` masks money and headcount, both
 * server-side. A Product Owner and the CIO can open the same screen and read
 * different numbers, which is the point. Nothing here changes that.
 *
 * ## Why permissions and not a list per person
 *
 * A per-person list of allowed pages drifts the moment a role changes: someone
 * gains `administer` and still cannot reach the governance queue because their
 * row was not updated. Deriving from permissions means granting the permission
 * is the whole change.
 */

export type ModuleGroup = 'summary' | 'analytics' | 'mapping' | 'discovery' | 'commercial' | 'data' | 'governance' | 'yours';

export interface ModuleRule {
  /** Route prefix under `/v2`, matched longest-first. */
  prefix: string;
  /** The permission a caller must hold to reach it. */
  requires: string;
  /** Shown when access is refused, in the reader's language. */
  reason: string;
}

/**
 * Longest prefix wins, so `/v2/admin/lineage` can differ from `/v2/admin`.
 * Anything unmatched requires `view`, which every role holds — a new screen is
 * readable by default and has to opt in to being restricted, rather than
 * becoming invisible to everyone the day it ships.
 */
export const MODULE_RULES: readonly ModuleRule[] = [
  {
    prefix: '/v2/admin/command-center',
    requires: 'administer',
    reason: 'The governance command centre is limited to platform administrators.',
  },
  {
    prefix: '/v2/admin/approvals',
    requires: 'approve',
    reason: 'Approvals are limited to roles that can approve a change.',
  },
  /*
    `/v2/collaboration` — "Decisions & actions" — is deliberately *not* listed,
    so it falls to the default `view`.

    It sits under the Governance heading in the rail, which makes it look like
    an administration screen, but it is the record of decisions taken and
    actions outstanding. A Product Owner reading the decisions that affect
    their own product is the point of keeping one; gating it on `approve`
    would hide from people the reasoning behind changes made to their estate.

    An earlier rule here pointed at `/v2/admin/decisions`, which is not a route
    this product has — it matched nothing and quietly did nothing, which is
    worse than an absent rule because it reads as a control that exists.
  */
  {
    prefix: '/v2/admin/taxonomy',
    requires: 'administer',
    reason: 'Controlled vocabularies are limited to platform administrators.',
  },
  {
    /*
      A field declared here appears in the import template every data owner in
      the estate is asked to fill in. That is a change to what the platform asks
      of people rather than a personal setting, which is why it sits with the
      other administrator surfaces — and the route is guarded as well as the
      button, because hiding a menu item is presentation.
    */
    prefix: '/v2/admin/fields',
    requires: 'administer',
    reason: 'Declaring a field for the whole tenant is limited to platform administrators.',
  },
  {
    prefix: '/v2/admin/roles',
    requires: 'administer',
    reason:
      'Roles and access are limited to platform administrators — a person cannot be given the ability to widen their own entitlements.',
  },
  {
    prefix: '/v2/admin/delegation',
    requires: 'administer',
    reason: 'Delegation is limited to platform administrators.',
  },
  {
    prefix: '/v2/admin/integrations',
    requires: 'administer',
    reason: 'Connectors are limited to platform administrators.',
  },
  {
    prefix: '/v2/admin/lineage',
    requires: 'administer',
    reason: 'Lineage and data quality are limited to platform administrators.',
  },
  {
    prefix: '/v2/data/import',
    requires: 'administer',
    reason:
      'Importing rewrites records across the whole tenant, so it is limited to platform administrators.',
  },
  {
    prefix: '/v2/data/exports',
    requires: 'export',
    reason: 'Exports are limited to roles that can take data out of the platform.',
  },
  {
    prefix: '/v2/scenarios',
    requires: 'propose',
    reason: 'Scenarios are limited to roles that can propose a change.',
  },
];

/** The permission a route needs. `view` when nothing more specific applies. */
export function requiredPermissionFor(path: string): ModuleRule | null {
  let best: ModuleRule | null = null;
  for (const rule of MODULE_RULES) {
    if (!path.startsWith(rule.prefix)) continue;
    if (!best || rule.prefix.length > best.prefix.length) best = rule;
  }
  return best;
}

/** Whether a caller holding `permissions` may reach `path`. */
export function canReach(path: string, permissions: readonly string[]): boolean {
  const rule = requiredPermissionFor(path);
  const needed = rule?.requires ?? 'view';
  return permissions.includes(needed);
}
